import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { del, get, apiRequest } from "../../lib/request";

function orgIdKey(organizationId) {
    if (organizationId == null || String(organizationId).trim() === "") return "";
    return String(organizationId).trim();
}

export const fetchOrganizations = createAsyncThunk(
    "organizations/fetchOrganizations",
    async ({ userId }, { rejectWithValue }) => {
        try {
            const response = await get(`organizations/users/${userId}`);
            return { userId: String(userId), organizations: response };
        } catch (error) {
            return rejectWithValue(error?.message || "API failed");
        }
    },
    {
        condition: ({ userId }, { getState }) => {
            const uid = userId != null ? String(userId).trim() : "";
            if (!uid) return false;
            const state = getState().organizations ?? {};
            if (state.organizationsListStatus === "loading") return false;
            if (
                state.organizationsListStatus === "succeeded" &&
                state.organizationsFetchedUserId === uid
            ) {
                return false;
            }
            return true;
        },
    },
);

export const fetchOrganizationsClients = createAsyncThunk(
    "organizations/fetchOrganizationsClients",
    async ({ organizationId }, { rejectWithValue }) => {
        try {
            const response = await get(`organizations/${organizationId}/stats`);
            return response;
        } catch (error) {
            return rejectWithValue(error?.message || "API failed");
        }
    },
    {
        condition: ({ organizationId, force }, { getState }) => {
            if (force) return true;
            const key = orgIdKey(organizationId);
            if (!key) return false;
            const state = getState().organizations ?? {};
            if (state.orgStatsStatus === "loading" && state.orgStatsOrganizationId === key) {
                return false;
            }
            if (
                state.orgStatsStatus === "succeeded" &&
                state.orgStatsOrganizationId === key
            ) {
                return false;
            }
            if (
                state.orgStatsStatus === "failed" &&
                state.orgStatsOrganizationId === key
            ) {
                return false;
            }
            return true;
        },
    },
);
export const fetchOrganizationsUserRole = createAsyncThunk(
    "organizations/fetchOrganizationsUserRole",
    async ({ organizationId }, { rejectWithValue }) => {
        try {
            const response = await get(`organizations/${organizationId}/account-users`);
           
            return response;
        } catch (error) {
            return rejectWithValue(error?.message || "API failed");
        }
    },
    {
        condition: ({ organizationId, force }, { getState }) => {
            if (force) return true;
            const key = orgIdKey(organizationId);
            if (!key) return false;
            const state = getState().organizations ?? {};
            if (
                state.accountUsersStatus === "loading" &&
                state.accountUsersOrganizationId === key
            ) {
                return false;
            }
            if (
                (state.accountUsersStatus === "succeeded" ||
                    state.accountUsersStatus === "failed") &&
                state.accountUsersOrganizationId === key
            ) {
                return false;
            }
            return true;
        },
    },
);
export const deleteAccountRole = createAsyncThunk(
    "organizations/deleteAccountRole",
    async ({ accountUserId }, { rejectWithValue }) => {
        try {
            
            const response = await del(`collaborators/${accountUserId}`);
            return { accountUserId }; // return ID so reducer can update state
        } catch (error) {
            return rejectWithValue(error?.message || "API failed");
        }
    }
);

export const deleteAdminAccountRole = createAsyncThunk(
    "organizations/deleteAdminAccountRole",
    async ({ accountUserId }, { rejectWithValue }) => {
        try {
            
            const response = await del(`organizations/users/${accountUserId}`);
            return { accountUserId }; // return ID so reducer can update state
        } catch (error) {
            return rejectWithValue(error?.message || "API failed");
        }
    }
);
export const deleteOrganizationsLogo = createAsyncThunk(
    "organizations/deleteOrganizationsLogo",
    async ({ organizationId }, { rejectWithValue }) => {
        try {
            const response = await del(`organizations/${organizationId}/logo`);
            return { organizationId }; // return ID so reducer can update state
        } catch (error) {
            return rejectWithValue(error?.message || "API failed");
        }
    }
);
export const addOrganizationsLogo = createAsyncThunk(
    "organizations/addOrganizationsLogo",
    async ({ id, file }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await apiRequest(`organizations/${id}/logo`, {
                method: "PATCH",
                body: formData,
            });

            return { id, logo: response };  // response = string URL
        } catch (error) {
            return rejectWithValue(error?.message || "API failed");
        }
    }
);
export const updateAccountRole = createAsyncThunk(
    "organizations/updateAccountRole",
    async ({ accountUserId, role }, { rejectWithValue }) => {
        try {
           
            const response = await apiRequest(
                `collaborators/${accountUserId}?role=${encodeURIComponent(role)}`,
                { method: "PATCH" }
            );

            return { accountUserId, role: response };
        } catch (error) {
            return rejectWithValue(error?.message || "API failed");
        }
    }
);

export const transferOrganizationsOwnership = createAsyncThunk(
    "organizations/transferOrganizationsOwnership",
    async ({ organizationId, userId }, { rejectWithValue }) => {
        try {

            const response = await apiRequest(`organizations/${organizationId}/users/${userId}/ownership/transfer`,
                { method: "PATCH" }
            );

            return { organizationId, userId };
        } catch (error) {
            return rejectWithValue(error?.message || "API failed");
        }
    }
);
export const updateOrganizationsName = createAsyncThunk(
    "organizations/updateOrganizationsName",
    async ({ organizationId, name }, { rejectWithValue }) => {
        try {
            const response = await apiRequest(
                `organizations/${organizationId}?name=${encodeURIComponent(name)}`,
                { method: "PATCH" }
            );

            return { organizationId, name };
        } catch (error) {
            return rejectWithValue(error?.message || "API failed");
        }
    }
);
// export const fetchMediaStats = createAsyncThunk(
//     "organizations/fetchMediaStats",
//     async ({ clientIds }, { getState, rejectWithValue }) => {
//         try {
//             const params = new URLSearchParams()
//             clientIds.forEach((id) => params.append("clientIds", id))

//             const response = await apiRequest(`/media/stats?${params.toString()}`, {
//                 method: "GET",
//             })

//             return response
//         } catch (error) {
//             return rejectWithValue(error.message || "Failed to load stats")
//         }
//     },
// )
const settingOrganizationSlice = createSlice({
    name: "organizations",
    initialState: {
        organizations: [],
        clients: [],
        organizationUserRole: null,
        loading: false,
        error: null,
        organizationsListStatus: "idle",
        organizationsFetchedUserId: null,
        accountUsersStatus: "idle",
        accountUsersOrganizationId: null,
        orgStatsStatus: "idle",
        orgStatsOrganizationId: null,
    },
    reducers: {
        resetOrganizationsState: () => ({
            organizations: [],
            clients: [],
            organizationUserRole: null,
            loading: false,
            error: null,
            organizationsListStatus: "idle",
            organizationsFetchedUserId: null,
            accountUsersStatus: "idle",
            accountUsersOrganizationId: null,
            orgStatsStatus: "idle",
            orgStatsOrganizationId: null,
        }),
    },
    extraReducers: (builder) => {
        builder
            .addCase(deleteAccountRole.fulfilled, (state, action) => {
                const { accountUserId } = action.payload;

                if (!state.organizationUserRole?.[0]?.organization?.users) return;

                state.organizationUserRole[0].organization.users.forEach(user => {
                    if (!user.accounts) return;

                    user.accounts = user.accounts.filter(
                        acc => acc.accountUserId !== accountUserId
                    );
                });
            })
            .addCase(deleteAdminAccountRole.fulfilled, (state, action) => {
    const { accountUserId } = action.payload;

    const orgData = state.organizationUserRole?.[0]?.organization;
    if (!orgData?.users) return;

    // remove user completely
    orgData.users = orgData.users.filter(
        user => String(user.userId) !== String(accountUserId)
    );
})

            .addCase(updateAccountRole.fulfilled, (state, action) => {
                const { accountUserId, role } = action.payload;

                // Find organization data
                const orgData = state.organizationUserRole?.[0]?.organization;
                if (!orgData || !orgData.users) return;

                // Find the user
                const user = orgData.users.find((u) => u.id === accountUserId);
                if (!user) return;

                // Update the role
                user.orgRole = role;  // ⭐ Update user role

            })

            // .addCase(fetchMediaStats.fulfilled, (state, action) => {
            //     const { clientId } = action.meta.arg;

            //     if (!state.mediaStats) {
            //         state.mediaStats = {};
            //     }

            //     state.mediaStats[clientId] = action.payload;
            // })

            .addCase(fetchOrganizationsUserRole.pending, (state, action) => {
                state.accountUsersStatus = "loading";
                state.accountUsersOrganizationId = orgIdKey(action.meta.arg?.organizationId);
            })
            .addCase(fetchOrganizationsUserRole.fulfilled, (state, action) => {
                state.organizationUserRole = action.payload;   // ⭐ STORE DATA HERE
                state.accountUsersStatus = "succeeded";
            })
            .addCase(fetchOrganizationsUserRole.rejected, (state, action) => {
                console.error("User role fetch failed", action.error);
                state.accountUsersStatus = "failed";
            })
            .addCase(fetchOrganizations.pending, (state) => {
                state.loading = true;
                state.organizationsListStatus = "loading";
            })
            .addCase(fetchOrganizations.fulfilled, (state, action) => {
                state.loading = false;
                state.organizationsListStatus = "succeeded";
                const payload = action.payload;
                const orgs =
                    payload && typeof payload === "object" && "organizations" in payload
                        ? payload.organizations
                        : payload;
                state.organizations = Array.isArray(orgs) ? orgs : [];
                if (payload?.userId != null) {
                    state.organizationsFetchedUserId = String(payload.userId);
                }
            })
            .addCase(fetchOrganizations.rejected, (state, action) => {
                state.loading = false;
                state.organizationsListStatus = "failed";
                state.error = action.payload;
            })

            .addCase(fetchOrganizationsClients.pending, (state, action) => {
                state.orgStatsStatus = "loading";
                state.orgStatsOrganizationId = orgIdKey(action.meta.arg?.organizationId);
            })
            .addCase(fetchOrganizationsClients.fulfilled, (state, action) => {
                state.clients = action.payload || [];
                state.orgStatsStatus = "succeeded";
            })
            .addCase(fetchOrganizationsClients.rejected, (state) => {
                state.orgStatsStatus = "failed";
            })

            .addCase(deleteOrganizationsLogo.fulfilled, (state, action) => {
                const id = action.payload.organizationId;

                const index = state.organizations.findIndex((o) => o.id === id);
                if (index !== -1) {
                    state.organizations[index].logo = null; // remove logo
                }
            })



            .addCase(addOrganizationsLogo.fulfilled, (state, action) => {
                const { id, logo } = action.payload;

                const i = state.organizations.findIndex(o => o.id === id);
                if (i !== -1) {
                    state.organizations[i].logo = logo;  // update logo url
                }
            })

            .addCase(updateOrganizationsName.fulfilled, (state, action) => {
                const { organizationId, name } = action.payload;

                const index = state.organizations.findIndex((o) => o.id === organizationId);
                if (index !== -1) {
                    state.organizations[index].name = name;
                }
            });
    },
});

export const { resetOrganizationsState } = settingOrganizationSlice.actions;
export default settingOrganizationSlice.reducer;
